import styled from '@emotion/styled'
import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { inScrollingLayout, scaledFontSize } from '../../style/scrollingLayout'
import Image from 'next/image'
import React, { useContext, useMemo } from 'react'
import {
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_DISCOUNT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_DISCOUNT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
    CustomizationContext,
} from '../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../context/viddyLPContext'
import type { ActiveLPLinkOptions } from '../../context/PageContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { hoverFill, onHover } from '../../style/interactiveStates'
import { belowFoldImageProps } from '../../utils/getFirstLcpImageSectionIndex'
import { getReviewSummary } from '../../utils/getReviewSummary'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import {
    getDisplayData,
    resolveProductCardImageDimensions,
    resolveProductCardImageUrl,
} from '../../utils/productDisplay'
import {
    priceFormat,
    formatPriceWithoutSymbol,
    splitDollarsAndCents,
} from '../../utils/priceFormat'
import { MIN_VISIBLE_REVIEW_COUNT } from '../../constants'
import { ReviewsStars } from '../landing-page/reviews/Reviews'
import {
    getNumberOfAvailableSwatchOptions,
    ProductGridColorSwatches,
} from './variant-options/ProductGridColorSwatches'
import { useProductGridVariantSelection } from './variant-options/useProductGridVariantSelection'
import { AddToCartInlineCTA } from '../landing-page/full-screen/AddToCart'
import type { ProductData } from '../landing-page/products/ProductContent'

const grey = '#D3D3D3' // TODO: where should this be defined?

const TruncatedTitle = styled.span({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    maxHeight: '2.4em', // 2 lines * 1.2em line-height
    ...getTypographyRoleStyle('productTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        lineHeight: '1.2em',
    }),
})

/**
 * The whole card opens the product. A wash over it would tint the photo it is
 * mostly made of, so the card's own edge carries the affordance.
 */
const LinkCardContainer = styled.div<{ background?: string }>(
    ({ background }) => ({
        height: 'auto',
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        alignItems: 'center',
        background: `${background ? background : `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`}`,
        padding: 10,
        borderRadius: '0.2rem',
        border: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
        pointerEvents: 'auto',
        cursor: 'pointer',
        ...onHover({
            borderColor: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 35%, transparent)`,
        }),
    }),
)

const LinkInfoContainer = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
})

const ProductCardImageContainer = styled.div({
    width: 120,
    flexShrink: 0,
    borderRadius: '0.2rem',
    overflow: 'hidden',
    // Grows with the card rather than to a fixed size, because the same card
    // is a wide block in a section and a narrow one in a horizontal rail.
    // The floor keeps the rail exactly as it is today.
    ...inScrollingLayout({ width: 'max(120px, 28%)' }),
})

const PRODUCT_CARD_IMAGE_DIMENSION = 1000

const CARD_CTA_MAX_WIDTH = 440

const productCardImageStyle = {
    width: '100%' as const,
    height: 'auto' as const,
    display: 'block' as const,
    borderRadius: '0.2rem',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
}

/**
 * Whether the price can sit beside the title depends on this column, not on
 * the window: the same card is a wide block in a page section and a narrow one
 * in the cart panel.
 */
const CARD_CONTAINER_NAME = 'product-card'

/** Below this the title and price collide, so the card stacks as on a phone. */
const SPLIT_CARD_MIN_PX = 380
const wideCard = `@container ${CARD_CONTAINER_NAME} (min-width: ${SPLIT_CARD_MIN_PX}px)`

const LinkTextContainer = styled.div({
    flex: 3,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingLeft: 10,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 300,
    }),
    fontSize: scaledFontSize(14),
    ...inScrollingLayout({
        containerType: 'inline-size',
        containerName: CARD_CONTAINER_NAME,
    }),
})

const CTAContainer = styled.div({
    width: '100%',
    marginTop: 'auto',
    // A button the width of a wide card reads as a banner.
    ...inScrollingLayout({
        maxWidth: CARD_CTA_MAX_WIDTH,
        marginInline: 'auto',
    }),
})

type CardArea = 'title' | 'rating' | 'deal' | 'price'

const Row = styled.div<{ area?: CardArea }>(({ area }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: 7,
    ...(area ? { [wideCard]: { gridArea: area } } : null),
}))

/**
 * With room to spare the price moves up beside the title rather than trailing
 * the details below it. `display: contents` leaves the rows as direct children
 * of the text column, so the stacked card is untouched.
 */
const CardHeader = styled.div({
    display: 'contents',
    [wideCard]: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto)',
        gridTemplateAreas: '"title price" "rating deal"',
        columnGap: 12,
        alignItems: 'start',
    },
})

const PriceRow = styled(Row)({
    [wideCard]: {
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
    },
})

const DealRow = styled(Row)({
    [wideCard]: {
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        rowGap: 2,
    },
})

export const CurrencySymbol = styled.span<{ marginTop?: number }>(
    ({ marginTop = 2 }) => ({
        ...getTypographyRoleStyle('price', {
            fontWeight: 400,
            lineHeight: 1,
        }),
        fontSize: scaledFontSize(10),
        alignSelf: 'flex-start',
        marginTop,
    }),
)

export const PriceDollars = styled.span({
    ...getTypographyRoleStyle('price', {
        fontWeight: 500,
        lineHeight: 1,
    }),
    fontSize: scaledFontSize(16),
})

export const PriceCents = styled.span<{ marginTop?: number }>(
    ({ marginTop = 2 }) => ({
        ...getTypographyRoleStyle('price', {
            fontWeight: 400,
            lineHeight: 1,
        }),
        fontSize: scaledFontSize(10),
        alignSelf: 'flex-start',
        marginTop,
    }),
)

/** `trailing` sits the badge after the price, on the card's outer edge. */
export const DiscountBadge = styled.div<{ trailing?: boolean }>(
    ({ trailing }) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: `var(${PRIMARY_DISCOUNT_COLOR_VAR})`,
        color: `var(${SECONDARY_DISCOUNT_COLOR_VAR})`,
        borderRadius: '0.2rem',
        marginRight: 5,
        width: 'fit-content',
        ...getTypographyRoleStyle('badgeText', {
            fontWeight: '100',
        }),
        fontSize: scaledFontSize(15),
        padding: '0 5px',
        ...(trailing
            ? {
                  [wideCard]: {
                      order: 1,
                      marginRight: 0,
                      marginLeft: 6,
                  },
              }
            : null),
    }),
)

export const DiscountText = styled.div({
    color: `var(${PRIMARY_DISCOUNT_COLOR_VAR})`,
    ...getTypographyRoleStyle('promoText', {
        fontWeight: 'bold',
        letterSpacing: '0.5px',
    }),
    fontSize: scaledFontSize(12),
    width: 'auto',
})

export const ListPriceLabel = styled.span({
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 300,
    }),
    fontSize: scaledFontSize(12),
})

export const ListPrice = styled.span({
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    ...getTypographyRoleStyle('compareAtPrice', {
        fontWeight: 300,
    }),
    fontSize: scaledFontSize(12),
    textDecoration: 'line-through',
})

const PRIMARY_CTA_OWN_PROPS = [
    'fillColor',
    'labelColor',
    'borderWidth',
    'isDisabled',
]

const PrimaryCTAButton = styled('div', {
    shouldForwardProp: (prop) => !PRIMARY_CTA_OWN_PROPS.includes(prop),
})<{
    fillColor: string
    labelColor: string
    borderWidth: string
    isDisabled: boolean
}>(({ fillColor, labelColor, borderWidth, isDisabled }) => ({
    flex: 1,
    backgroundColor: isDisabled ? grey : fillColor,
    border: `${borderWidth} solid ${labelColor}`,
    borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 35,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    color: labelColor,
    ...getTypographyRoleStyle('buttonText', { fontWeight: 400 }),
    fontSize: scaledFontSize(13),
    ...(!isDisabled && hoverFill(fillColor, labelColor)),
}))

export const PrimaryCTA: React.FC<{
    onClick: () => void
    text: string
    buttonColor?: string
    textColor?: string
    disabled?: boolean
    borderWidth?: string
}> = ({
    onClick,
    text,
    buttonColor,
    textColor,
    disabled,
    borderWidth = '1px',
}) => {
    const { secondaryCtaFillColor, secondaryCtaTextColor } =
        useContext(CustomizationContext)

    const _onClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onClick()
    }

    // Resolved values rather than the custom properties that carry them, so
    // the hover rule can tell a white button from a dark one. Same source.
    const fillColor = buttonColor ?? secondaryCtaFillColor
    const labelColor = textColor ?? secondaryCtaTextColor
    return (
        <PrimaryCTAButton
            onClick={disabled ? () => {} : _onClick}
            aria-disabled={disabled}
            fillColor={fillColor}
            labelColor={labelColor}
            borderWidth={borderWidth}
            isDisabled={Boolean(disabled)}
        >
            {disabled ? 'Out of Stock' : text}
        </PrimaryCTAButton>
    )
}

interface ProductCardProps {
    lpLink: ApiShopperLandingPageLink
    onClick: (
        e: React.MouseEvent<HTMLDivElement>,
        variantCtx: ActiveLPLinkOptions,
    ) => void
    background?: string
    buttonColor?: string
    buttonTextColor?: string
    color?: string
    border?: string
    buttonBorderWidth?: string
    showRatingCount?: boolean
    showLimitedTimeOffer?: boolean
    showCTA?: boolean
    ctaText?: string
    onCtaClick?: (variantCtx: ActiveLPLinkOptions) => void
    priceMarginTop?: number
    showSuperscriptPriceFormat?: boolean
    /**
     * Prefer an available variant for `activeVariant` / cart context when the card has no
     * swatch row (`enabled` false), e.g. cart upsells. Swatch grids set this via the hook when `enabled`.
     */
    preferAvailable?: boolean
    /** When true, renders an inline add-to-cart CTA matching the PLP grid behavior. */
    showAddToCart?: boolean
    /**
     * Custom CTA renderer, useful when the caller needs the card's live variant
     * state (e.g. cart upsells opening a variant-select modal seeded with the
     * shopper's swatch choice). When provided, takes precedence over
     * `showAddToCart` and `showCTA`.
     */
    renderCTA?: (args: {
        rowProductData: ProductData
        ctaDisabled: boolean
    }) => React.ReactNode
    deprioritizeImage?: boolean
}

interface PriceDisplayProps {
    price?: number
    compareAtPriceNumber?: number | null
    currencySymbol?: string
    priceMarginTop?: number
}

const usePriceDisplay = ({
    price,
    compareAtPriceNumber,
    currencySymbol = '$',
}: PriceDisplayProps) => {
    const { dollars, cents } = useMemo(() => {
        if (price === undefined) {
            return { dollars: '', cents: '' }
        }
        const stringPrice = formatPriceWithoutSymbol(price)
        return splitDollarsAndCents(stringPrice)
    }, [price])

    const compareAtPrice = useMemo(() => {
        if (!compareAtPriceNumber) {
            return null
        }
        return priceFormat(compareAtPriceNumber, currencySymbol)
    }, [compareAtPriceNumber, currencySymbol])

    const shouldShowComparePrice =
        compareAtPrice !== null &&
        compareAtPriceNumber != null &&
        price !== undefined &&
        compareAtPriceNumber > price

    return {
        dollars,
        cents,
        compareAtPrice,
        shouldShowComparePrice,
    }
}

const NormalPriceDisplay: React.FC<PriceDisplayProps> = ({
    price,
    compareAtPriceNumber,
    currencySymbol = '$',
}) => {
    const { dollars, cents, compareAtPrice, shouldShowComparePrice } =
        usePriceDisplay({ price, compareAtPriceNumber, currencySymbol })

    return (
        <>
            <span
                style={{
                    ...getTypographyRoleStyle('price', {
                        fontWeight: 500,
                        lineHeight: 1,
                    }),
                    fontSize: scaledFontSize(16),
                    marginRight: '10px',
                }}
            >
                {currencySymbol}
                {dollars}.{cents}
            </span>
            {shouldShowComparePrice && (
                <ListPriceLabel>
                    <ListPrice>{compareAtPrice}</ListPrice>
                </ListPriceLabel>
            )}
        </>
    )
}

const SuperscriptPriceDisplay: React.FC<PriceDisplayProps> = ({
    price,
    compareAtPriceNumber,
    currencySymbol = '$',
    priceMarginTop,
}) => {
    const { dollars, cents, compareAtPrice, shouldShowComparePrice } =
        usePriceDisplay({ price, compareAtPriceNumber, currencySymbol })

    return (
        <>
            <CurrencySymbol marginTop={priceMarginTop}>
                {currencySymbol}
            </CurrencySymbol>
            <PriceDollars>{dollars}</PriceDollars>
            <PriceCents
                marginTop={priceMarginTop}
                style={{ marginRight: '10px' }}
            >
                {cents}
            </PriceCents>
            {shouldShowComparePrice && (
                <ListPriceLabel>
                    <ListPrice>{compareAtPrice}</ListPrice>
                </ListPriceLabel>
            )}
        </>
    )
}

const PriceDisplay: React.FC<
    PriceDisplayProps & { showSuperscriptPriceFormat?: boolean }
> = ({ showSuperscriptPriceFormat = true, ...props }) => {
    if (!showSuperscriptPriceFormat) {
        return <NormalPriceDisplay {...props} />
    }
    return <SuperscriptPriceDisplay {...props} />
}

// eslint-disable-next-line complexity
export const ProductCard: React.FC<ProductCardProps> = ({
    lpLink,
    onClick,
    background,
    buttonColor,
    color,
    border,
    buttonTextColor,
    buttonBorderWidth,
    showRatingCount = true,
    showLimitedTimeOffer = true,
    showCTA = false,
    ctaText,
    onCtaClick,
    priceMarginTop,
    showSuperscriptPriceFormat = true,
    preferAvailable = false,
    showAddToCart = false,
    renderCTA,
    deprioritizeImage = true,
}) => {
    const { landingPage } = useContext(ViddyLPContext)

    /** Only for swatch UI + selection handlers; pricing/image/title still use `activeVariant` when this is false. */
    const hasSwatchOption = getNumberOfAvailableSwatchOptions(lpLink) > 1

    const {
        selectedOptions,
        handleOptionSelect,
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    } = useProductGridVariantSelection({
        lpLink: lpLink,
        showSwatches: hasSwatchOption,
        preferAvailable,
    })

    // Variant-aware display data for virtual products (title, reviews, carousel, etc.)
    // `activeVariant` always comes from useProductGridVariantSelection / useProductData (swatches on or off).
    const displayData = useMemo(
        () => getDisplayData(lpLink, activeVariant?.shopifyProductId ?? null),
        [lpLink, activeVariant],
    )

    const sourceImage = resolveProductCardImageUrl(
        activeVariant,
        lpLink,
        displayData,
    )
    const imageDimensions = resolveProductCardImageDimensions(
        sourceImage,
        activeVariant,
        lpLink,
    )
    const image = imageUrlTransform(sourceImage)

    const displayTitle = displayData.title ?? lpLink.title
    const altText = activeVariant
        ? activeVariant.title || displayTitle || 'Product Image'
        : displayTitle || 'Product Image'

    const { reviewRating, reviewCount } =
        landingPage == null
            ? { reviewRating: 0, reviewCount: 0 }
            : getReviewSummary(landingPage, {
                  ...lpLink,
                  reviews: displayData.reviews ?? lpLink.reviews,
              })

    const variantCtx: ActiveLPLinkOptions = useMemo(
        () => ({ initialVariantShopifyId: activeVariant?.shopifyId ?? null }),
        [activeVariant?.shopifyId],
    )

    const ctaDisabled = activeVariant
        ? !activeVariant.available
        : !(lpLink.variantInfo ?? []).some((variant) => variant.available)

    const rowProductData = useMemo(
        (): ProductData => ({
            activeVariant,
            setActiveVariant,
            reviewsRef,
            price,
            comparePrice,
            discountPercentage,
            selectedSellingPlanId,
            setSelectedSellingPlanId,
        }),
        [
            activeVariant,
            setActiveVariant,
            reviewsRef,
            price,
            comparePrice,
            discountPercentage,
            selectedSellingPlanId,
            setSelectedSellingPlanId,
        ],
    )

    return (
        <LinkCardContainer
            style={{ flex: 3.5, background, color, border, padding: '.75rem' }}
            onClick={(e) =>
                onClick(e as React.MouseEvent<HTMLDivElement>, variantCtx)
            }
        >
            <LinkInfoContainer>
                <ProductCardImageContainer>
                    <Image
                        src={image}
                        alt={altText}
                        width={
                            imageDimensions?.width ??
                            PRODUCT_CARD_IMAGE_DIMENSION
                        }
                        height={
                            imageDimensions?.height ??
                            PRODUCT_CARD_IMAGE_DIMENSION
                        }
                        sizes="120px"
                        style={productCardImageStyle}
                        {...belowFoldImageProps(deprioritizeImage)}
                    />
                </ProductCardImageContainer>
                <LinkTextContainer style={{ color }}>
                    <CardHeader>
                        <Row area="title">
                            <TruncatedTitle>{displayTitle}</TruncatedTitle>
                        </Row>
                        {reviewCount != null &&
                            reviewRating != null &&
                            reviewCount > MIN_VISIBLE_REVIEW_COUNT && (
                                <Row area="rating">
                                    <ReviewsStars
                                        rating={reviewRating}
                                        starSize={11}
                                        fontSize={12}
                                        count={reviewCount}
                                        starColor={color}
                                        textColor={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                                        showRating={showRatingCount}
                                    />
                                </Row>
                            )}
                        {discountPercentage != null &&
                            discountPercentage > 0 &&
                            showLimitedTimeOffer && (
                                <DealRow area="deal">
                                    <DiscountBadge>
                                        -{discountPercentage}%
                                    </DiscountBadge>
                                    <DiscountText>
                                        Limited time deal
                                    </DiscountText>
                                </DealRow>
                            )}
                        <PriceRow area="price">
                            {discountPercentage != null &&
                                discountPercentage > 0 &&
                                !showLimitedTimeOffer && (
                                    <DiscountBadge trailing>
                                        -{discountPercentage}%
                                    </DiscountBadge>
                                )}

                            <PriceDisplay
                                price={price}
                                compareAtPriceNumber={comparePrice}
                                currencySymbol={
                                    landingPage?.client?.currencySymbol || '$'
                                }
                                priceMarginTop={priceMarginTop}
                                showSuperscriptPriceFormat={
                                    showSuperscriptPriceFormat
                                }
                            />
                        </PriceRow>
                    </CardHeader>
                    {hasSwatchOption && (
                        <Row>
                            <ProductGridColorSwatches
                                lpLink={lpLink}
                                selectedOptions={selectedOptions}
                                onOptionSelect={handleOptionSelect}
                            />
                        </Row>
                    )}
                    {renderCTA ? (
                        <CTAContainer onClick={(e) => e.stopPropagation()}>
                            {renderCTA({ rowProductData, ctaDisabled })}
                        </CTAContainer>
                    ) : showAddToCart ? (
                        <CTAContainer onClick={(e) => e.stopPropagation()}>
                            <AddToCartInlineCTA
                                link={lpLink}
                                rowProductData={rowProductData}
                            />
                        </CTAContainer>
                    ) : (
                        showCTA &&
                        ctaText &&
                        onCtaClick && (
                            <CTAContainer>
                                <PrimaryCTA
                                    buttonColor={buttonColor}
                                    textColor={buttonTextColor}
                                    borderWidth={buttonBorderWidth}
                                    text={ctaText}
                                    onClick={() => onCtaClick(variantCtx)}
                                    disabled={ctaDisabled}
                                />
                            </CTAContainer>
                        )
                    )}
                </LinkTextContainer>
            </LinkInfoContainer>
        </LinkCardContainer>
    )
}
